/**
 * UserMultiSelect Molecule
 *
 * Componente para buscar e selecionar múltiplos usuários
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, User, Check } from 'lucide-react'
import { usersApi } from '../../core/admin.api'
import Avatar from '../atoms/Avatar'
import Badge from '../atoms/Badge'
import Spinner from '../atoms/Spinner'

export default function UserMultiSelect({
	value = [],
	onChange,
	placeholder = 'Buscar usuários por nome ou email...',
	maxSelections = null,
	className = '',
}) {
	const [search, setSearch] = useState('')
	const [results, setResults] = useState([])
	const [loading, setLoading] = useState(false)
	const [isOpen, setIsOpen] = useState(false)
	const [selectedUsers, setSelectedUsers] = useState([])
	const containerRef = useRef(null)
	const inputRef = useRef(null)

	// Buscar usuários com debounce
	useEffect(() => {
		if (!search || search.length < 2) {
			setResults([])
			return
		}

		const timer = setTimeout(async () => {
			setLoading(true)
			try {
				const response = await usersApi.list({ search, limit: 10 })
				const users = response?.data || []
				// Filtrar usuários já selecionados
				const filtered = users.filter(
					(user) => !value.includes(user._id)
				)
				setResults(filtered)
			} catch (err) {
				console.error('Erro ao buscar usuários:', err)
				setResults([])
			} finally {
				setLoading(false)
			}
		}, 300)

		return () => clearTimeout(timer)
	}, [search, value])

	// Carregar dados dos usuários selecionados
	useEffect(() => {
		const loadSelectedUsers = async () => {
			if (value.length === 0) {
				setSelectedUsers([])
				return
			}

			// Só buscar se temos IDs mas não temos os dados
			const missingIds = value.filter(
				(id) => !selectedUsers.find((u) => u._id === id)
			)

			if (missingIds.length > 0) {
				try {
					const response = await usersApi.list({
						ids: missingIds.join(','),
						limit: missingIds.length
					})
					const users = response?.data || []
					setSelectedUsers((prev) => {
						const existing = prev.filter((u) => value.includes(u._id))
						return [...existing, ...users]
					})
				} catch (err) {
					console.error('Erro ao carregar usuários selecionados:', err)
				}
			}
		}

		loadSelectedUsers()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value])

	// Fechar dropdown ao clicar fora
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleSelect = useCallback((user) => {
		if (maxSelections && value.length >= maxSelections) {
			return
		}

		const newValue = [...value, user._id]
		setSelectedUsers((prev) => [...prev, user])
		onChange?.(newValue)
		setSearch('')
		setResults([])
		inputRef.current?.focus()
	}, [value, maxSelections, onChange])

	const handleRemove = useCallback((userId) => {
		const newValue = value.filter((id) => id !== userId)
		setSelectedUsers((prev) => prev.filter((u) => u._id !== userId))
		onChange?.(newValue)
	}, [value, onChange])

	const handleClearAll = useCallback(() => {
		setSelectedUsers([])
		onChange?.([])
	}, [onChange])

	return (
		<div ref={containerRef} className={`relative ${className}`}>
			{/* Label */}
			<label className="block text-sm font-medium text-text-body mb-1">
				Usuários selecionados
				{maxSelections && (
					<span className="text-text-muted ml-1">
						({value.length}/{maxSelections})
					</span>
				)}
			</label>

			{/* Selected Users */}
			{selectedUsers.length > 0 && (
				<div className="flex flex-wrap gap-2 mb-2 p-2 bg-base-400 rounded-lg">
					{selectedUsers.filter((u) => value.includes(u._id)).map((user) => (
						<Badge
							key={user._id}
							variant="primary"
							className="flex items-center gap-1 pr-1"
						>
							<span className="truncate max-w-[150px]">
								{user.name} {user.lastname}
							</span>
							<button
								type="button"
								onClick={() => handleRemove(user._id)}
								className="ml-1 p-0.5 hover:bg-primary-600 rounded-full transition-colors"
							>
								<X className="w-3 h-3" />
							</button>
						</Badge>
					))}
					{value.length > 1 && (
						<button
							type="button"
							onClick={handleClearAll}
							className="text-xs text-danger hover:text-danger-600 ml-auto"
						>
							Limpar todos
						</button>
					)}
				</div>
			)}

			{/* Search Input */}
			<div className="relative">
				<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
					{loading ? (
						<Spinner size="sm" />
					) : (
						<Search className="h-5 w-5 text-text-muted" />
					)}
				</div>
				<input
					ref={inputRef}
					type="text"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onFocus={() => setIsOpen(true)}
					placeholder={placeholder}
					disabled={maxSelections && value.length >= maxSelections}
					className={`
						block w-full rounded-lg border border-base-200
						bg-base-300 pl-10 pr-4 py-2.5 text-text
						placeholder:text-text-muted
						focus:border-primary focus:ring-2 focus:ring-primary/20
						disabled:bg-base-200 disabled:cursor-not-allowed
					`}
				/>
			</div>

			{/* Dropdown Results */}
			{isOpen && search.length >= 2 && (
				<div className="absolute z-50 w-full mt-1 bg-base-300 border border-base-200 rounded-lg shadow-lg max-h-60 overflow-auto">
					{loading ? (
						<div className="p-4 text-center text-text-muted">
							<Spinner size="sm" className="mx-auto mb-2" />
							Buscando...
						</div>
					) : results.length > 0 ? (
						<ul className="py-1">
							{results.map((user) => (
								<li key={user._id}>
									<button
										type="button"
										onClick={() => handleSelect(user)}
										className="w-full px-4 py-2 flex items-center gap-3 hover:bg-base-400 transition-colors text-left"
									>
										<Avatar
											src={user.picture}
											name={`${user.name} ${user.lastname}`}
											size="sm"
										/>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-text truncate">
												{user.name} {user.lastname}
											</p>
											<p className="text-xs text-text-muted truncate">
												{user.email}
											</p>
										</div>
										{value.includes(user._id) && (
											<Check className="w-4 h-4 text-success" />
										)}
									</button>
								</li>
							))}
						</ul>
					) : (
						<div className="p-4 text-center text-text-muted">
							<User className="w-8 h-8 mx-auto mb-2 opacity-50" />
							<p className="text-sm">Nenhum usuário encontrado</p>
							<p className="text-xs">Tente outro termo de busca</p>
						</div>
					)}
				</div>
			)}

			{/* Help text */}
			{!isOpen && value.length === 0 && (
				<p className="mt-1 text-xs text-text-muted">
					Digite pelo menos 2 caracteres para buscar
				</p>
			)}
		</div>
	)
}
